import { useAuth0 } from "@appretio/auth";
import { useTenantContext } from "@appretio/shared";
import { useCallback, useMemo, useState } from "react";
import { collectClientInfo } from "../clientInfo";
import { fitContextToBudget } from "../contextBudget";
import { useFeedback } from "../FeedbackContext";
import { CELL_KEY } from "../feedbackViewRestore";

/**
 * Page (pathname+search) budget — mirrors the API's MAX_PAGE_CHARS. Clipped
 * client-side so a long query string can never make feedback unsendable
 * (the server rejects, not truncates, an over-long page).
 */
const MAX_PAGE_CHARS = 400;

/** Everything a report carries besides the user's own category and message. */
export interface FeedbackPayload {
  /** Current location (pathname+search), clipped to the API's budget. */
  page: string;
  /** Merged page-context blocks, fitted to the API's serialized budget. */
  context: Record<string, unknown>;
  /** Browser/platform facts plus the client's own identity echo. */
  client: Record<string, unknown>;
}

/** The capture the feedback modal submits, and previews before submitting. */
export interface FeedbackPayloadCapture {
  /**
   * The capture as of the last (re)collection — what the transparency pane
   * discloses. Null while the modal is closed.
   */
  preview: FeedbackPayload | null;
  /**
   * Collect a FRESH capture. Called at Send time so the report carries
   * submit-time state (page getters, diagnostics, URL, client clock) rather
   * than modal-open state.
   */
  capture: () => FeedbackPayload;
  /** Re-collect {@link preview} — call when the disclosure is expanded. */
  recapture: () => void;
}

/**
 * The feedback capture pipeline: what gets attached to a report, budgeted to
 * the API's limits. Split out of the modal so the modal owns only the form
 * (docs/code-guidelines/components.md — "Extract when a component has
 * multiple responsibilities").
 */
export function useFeedbackPayload(): FeedbackPayloadCapture {
  const { isOpen, prefill, collectPageContext } = useFeedback();
  const { currentOrg } = useTenantContext();
  // Who the CLIENT believes it is (Auth0 profile) — the API stores the
  // server-resolved identity under `context.user`; diffing the two catches
  // token/role drift, which is itself a bug class. Null when auth is
  // disabled locally (no Auth0 session to echo).
  const { user: authUser } = useAuth0();

  const capture = useCallback((): FeedbackPayload => {
    // The Auth0 profile's sub plus any namespaced roles claim — enough to
    // diff against the server-resolved `context.user`, nothing more.
    const rolesClaim = authUser
      ? Object.entries(authUser).find(([key]) => key.endsWith("/roles"))?.[1]
      : undefined;
    const identity = authUser
      ? {
          sub: authUser.sub ?? null,
          ...(Array.isArray(rolesClaim) ? { roles: rolesClaim } : {})
        }
      : null;
    return {
      // Clipped to the API's budget — over-long query strings must degrade
      // (truncated page), never reject the whole report.
      page: (window.location.pathname + window.location.search).slice(
        0,
        MAX_PAGE_CHARS
      ),
      // Fitted to the API's serialized budget — an oversized capture drops
      // its largest blocks (with a `truncated` marker) rather than 400ing.
      context: fitContextToBudget({
        ...(currentOrg ? { tenant: currentOrg.name } : {}),
        ...collectPageContext(),
        ...(prefill?.cell ? { [CELL_KEY]: prefill.cell } : {})
      }),
      client: { ...collectClientInfo(), identity }
    };
  }, [authUser, currentOrg, collectPageContext, prefill]);

  // `previewTick` re-collects the capture on demand (the disclosure opening),
  // so what the pane discloses matches what a Send at that moment would
  // actually submit.
  const [previewTick, setPreviewTick] = useState(0);
  const preview = useMemo(() => {
    void previewTick; // re-capture on each disclosure open
    return isOpen ? capture() : null;
  }, [isOpen, capture, previewTick]);

  const recapture = useCallback(() => setPreviewTick((tick) => tick + 1), []);

  return { preview, capture, recapture };
}
